import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";


export default function IceCreamPOS({ open, onClose, product, products = [], onAddToCart }) {
  // Config del producto final (viene de ProductForm → config)
  const cfg = product?.config || {};
  const saboresRequeridos = Number(cfg.sabores || 1);
  const gramosPorSabor = Number(cfg.gramosPorSabor || 90);
  const basePreferida = (cfg.base || "cono").toLowerCase(); // "cono" | "vaso"
  const extraItems = Array.isArray(cfg.extraItems) ? cfg.extraItems : [];

  // Listas de INSUMOS
  const insumosUnidad = useMemo(
    () => products.filter(p => p.type === "insumo" && p.unit === "unidad"),
    [products]
  );
  const insumosGramo = useMemo(
    () => products.filter(p => p.type === "insumo" && p.unit === "g"),
    [products]
  );

  // Detectar insumos por nombre (puedes ajustar si usas categorías específicas)
const baseInsumo = useMemo(() => {
  if (cfg.baseInsumoId) {
    const byId = insumosUnidad.find(p => p.id === cfg.baseInsumoId);
    if (byId) return byId;
  }
  const prefer = (cfg.base || 'vaso').toLowerCase();
  return insumosUnidad.find(p => (p.name || '').toLowerCase().includes(prefer)) || null;
}, [insumosUnidad, cfg.baseInsumoId, cfg.base]);

  const servilletaInsumo = useMemo(
    () => insumosUnidad.find(p => p.name.toLowerCase().includes("servillet")) || null,
    [insumosUnidad]
  );
  const cucharaInsumo = useMemo(
    () => insumosUnidad.find(p => p.name.toLowerCase().includes("cuchar")) || null,
    [insumosUnidad]
  );

// Normalizador simple (sin tildes, minúsculas)
const norm = (s = '') =>
  s.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Catálogo de sabores: solo insumos en gramos cuyo nombre empieza por "helado"
const catalogoSabores = useMemo(
  () =>
    insumosGramo
      .filter(p => norm(p.name).startsWith('helado'))  // 👈 aquí el filtro
      .sort((a, b) => norm(a.name).localeCompare(norm(b.name))),
  [insumosGramo]
);

  // Estados de selección de sabores
  const [saboresSel, setSaboresSel] = useState(Array.from({ length: saboresRequeridos }, () => ""));

  // reemplaza la línea que define basePreferida si quieres, o déjala; usamos un estado basado en ella
const [baseType, setBaseType] = useState((cfg.base || "cono").toLowerCase()); // 'cono' | 'vaso'


  useEffect(() => {
    setSaboresSel(Array.from({ length: saboresRequeridos }, () => ""));
  }, [saboresRequeridos, product?.id]);

  const canAgregar = useMemo(() => {
    // Debe haber base y todos los sabores seleccionados
    if (!baseInsumo) return false;
    if (saboresSel.some(id => !id)) return false;
    return true;
  }, [baseInsumo, saboresSel]);

  const handleChangeSabor = (index, id) => {
    setSaboresSel(prev => {
const next = [...prev]; 
next[index] = id;
      return next;
    });
  };

  const handleAdd = () => {
    if (!canAgregar) {
      alert("Completa la base y los sabores.");
      return;
    }

    // Construir componentes de receta
    const comps = [];

    // Base: 1 unidad (cono o vaso)
    comps.push({
      insumoId: baseInsumo.id,
      name: baseInsumo.name,
      unit: "unidad",
      qtyPerUnit: 1,
      costPrice: Number(baseInsumo.costPrice || 0),
    });

    // Servilletas/cucharas si tu config lo pide y los insumos existen
    const servCount = Number(cfg.servilletas || 0);
    if (servCount > 0 && servilletaInsumo) {
      comps.push({
        insumoId: servilletaInsumo.id,
        name: servilletaInsumo.name,
        unit: "unidad",
        qtyPerUnit: servCount,
        costPrice: Number(servilletaInsumo.costPrice || 0),
      });
    }
    const cuchCount = Number(cfg.cucharas || 0);
    if (cuchCount > 0 && cucharaInsumo) {
      comps.push({
        insumoId: cucharaInsumo.id,
        name: cucharaInsumo.name,
        unit: "unidad",
        qtyPerUnit: cuchCount,
        costPrice: Number(cucharaInsumo.costPrice || 0),
      });
    }

    extraItems.forEach((item) => {
      const insumo = products.find((p) => p.id === item.productId);
      if (!insumo) return;
      comps.push({
        insumoId: insumo.id,
        name: insumo.name,
        unit: insumo.unit || item.unit || "unidad",
        qtyPerUnit: Number(item.qty || 0),
        costPrice: Number(insumo.costPrice || 0),
      });
    });

    // Sabores: cada uno con "gramosPorSabor"
    const saboresNombres = [];
    saboresSel.forEach(id => {
      const s = catalogoSabores.find(x => x.id === id);
      if (s) {
        saboresNombres.push(s.name);
        comps.push({
          insumoId: s.id,
          name: s.name,
          unit: "g",
          qtyPerUnit: gramosPorSabor,
          costPrice: Number(s.costPrice || 0),
        });
      }
    });

    const etiquetaSabores = saboresNombres.join(" / ");
    const itemParaCarrito = {
      id: product.id,               // mismo id del producto final
      name: `${product.name} - ${etiquetaSabores}`,
      image: product.image || "",
      price: Number(product.price || 0),
      quantity: 1,
      // MARCA que es “receta dinámica” → POSView ya resta insumos y NO descuenta del producto final.
      recipeComponents: comps
    };

    onAddToCart(itemParaCarrito);
    onClose();
  };

 const modal = (
  <div
   key={`${product?.id || 'ice'}-${saboresRequeridos}`}  // 🔑 fuerza remonte si cambia #sabores o producto
 className={`fixed inset-0 bg-black/40 z-50 flex items-center justify-center ${open ? '' : 'hidden'}`}
   >   
     <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold">Configurar {product?.name || "Helado"}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {!baseInsumo && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            No se encontró un insumo para la base “{basePreferida}”. Crea un insumo con nombre que contenga “{basePreferida}” y unidad “unidad”.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">
              Base: <strong className="text-gray-900">{baseInsumo ? baseInsumo.name : "—"}</strong>
            </p>
          </div>

          {Array.from({ length: saboresRequeridos }).map((_, i) => (
            <div key={i}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sabor #{i + 1} ({gramosPorSabor} g)
              </label>
              <select
                value={saboresSel[i]}
                onChange={(e) => handleChangeSabor(i, e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Seleccionar sabor...</option>
                {catalogoSabores.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          ))}

          <div className="text-sm text-gray-600">
            {cfg.servilletas > 0 && <p>Servilletas: {cfg.servilletas} {servilletaInsumo ? `(${servilletaInsumo.name})` : "(falta insumo)"} </p>}
            {cfg.cucharas > 0 && <p>Cucharas: {cfg.cucharas} {cucharaInsumo ? `(${cucharaInsumo.name})` : "(falta insumo)"} </p>}
            {extraItems.map((item, index) => (
              <p key={`${item.productId || item.name || 'extra'}-${index}`}>
                Extra fijo: {item.name} ({item.qty} {item.unit})
              </p>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancelar</button>
          <button
            onClick={handleAdd}
            disabled={!canAgregar}
            className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            Agregar al carrito
          </button>
        </div>
      </div>
    </div>
);

 // Render en portal para evitar reconciliación rara dentro del árbol del POS
 return createPortal(modal, document.body);
}
